// Buyer-data fields (name, email, phone with country, DNI, optional notes) shared by the normal court
// booking form (desktop and mobile), the open-match create flow and the open-match join form.
// Presentational: the parent owns the react-hook-form instance (`register`, `errors`, `trigger`) and the
// selected country. `variant` only picks the class family ("desktop" = `.field`, "mobile" = `.rmob-field`);
// validation rules are identical everywhere. The country dropdown keeps its own open/close state.
import React, { useEffect, useRef, useState } from "react";
import RIcon from "../../reservas/components/ReservasIcons";
import { COUNTRIES } from "../../reservas/components/ReservaShared";

/**
 * Splits a stored phone (e.g. "+54 11 5555 1234") into a country and the local digits the form expects.
 * Returns an empty phone when it cannot be matched to a valid number, so the user types it instead.
 */
export const splitPhone = (raw) => {
	const digits = String(raw || "").replace(/\D/g, "");
	if (!digits) return { country: COUNTRIES[0], phone: "" };
	if (String(raw).trim().startsWith("+")) {
		const byPrefix = [...COUNTRIES].sort((a, b) => b.prefix.length - a.prefix.length);
		for (const c of byPrefix) {
			const p = c.prefix.replace(/\D/g, "");
			if (digits.startsWith(p) && c.validate(digits.slice(p.length))) return { country: c, phone: digits.slice(p.length) };
		}
	}
	if (COUNTRIES[0].validate(digits)) return { country: COUNTRIES[0], phone: digits };
	return { country: COUNTRIES[0], phone: "" };
};

const ERR_STYLE = { fontSize: 12, color: "#e53935" };

const BuyerDataFields = ({
	register,
	errors,
	trigger,
	country,
	onCountryChange,
	variant = "desktop",
	showNotes = false,
	notesPlaceholder = "Cumpleaños, alergias, preferencias…",
	emailHint = null,
}) => {
	const mobile = variant === "mobile";
	const [open, setOpen] = useState(false);
	const ref = useRef(null);

	useEffect(() => {
		const onDoc = (e) => {
			if (ref.current && !ref.current.contains(e.target)) setOpen(false);
		};
		document.addEventListener("mousedown", onDoc);
		return () => document.removeEventListener("mousedown", onDoc);
	}, []);

	const fieldCls = (full) => (mobile ? "rmob-field" : `field${full ? " full" : ""}`);
	const err = (e, extra) =>
		e ? (mobile ? <span className="rmob-field-error">{e.message}</span> : <span style={{ ...ERR_STYLE, ...extra }}>{e.message}</span>) : null;

	return (
		<>
			<div className={fieldCls(true)}>
				<label>Nombre y apellido <span className="req">*</span></label>
				<div className="inp">
					<RIcon name="user" />
					<input placeholder="Ej. Camila Rodríguez" {...register("name", { required: "Ingresá tu nombre." })} />
				</div>
				{err(errors.name)}
			</div>
			<div className={fieldCls(false)}>
				<label>Email <span className="req">*</span></label>
				<div className="inp">
					<RIcon name="mail" />
					<input
						type="email"
						placeholder="tu@email.com"
						{...register("email", {
							required: "Ingresá un email válido.",
							pattern: { value: /^\S+@\S+\.\S+$/, message: "Email inválido." },
						})}
					/>
				</div>
				{emailHint && <span className="hint">{emailHint}</span>}
				{err(errors.email)}
			</div>
			<div className={fieldCls(false)}>
				<label>Teléfono <span className="req">*</span></label>
				<div className={`inp${errors.phone ? " is-invalid" : ""}`} style={{ paddingLeft: 0, position: "relative" }}>
					<div
						ref={ref}
						style={{
							display: "flex", alignItems: "center", gap: 8, paddingLeft: 14, paddingRight: 12, height: "100%",
							background: "#f9fafb", borderTopLeftRadius: 9, borderBottomLeftRadius: 9, userSelect: "none",
							borderRight: "1px solid var(--zoco-border, #E5E7EB)", flexShrink: 0, cursor: "pointer", position: "relative",
						}}
						onClick={() => setOpen((o) => !o)}
					>
						{country.flag}
						<span style={{ fontSize: 13, fontWeight: 700, color: "#2d3035", whiteSpace: "nowrap" }}>{country.label}</span>
						<svg width="8" height="5" viewBox="0 0 8 5" fill="none" style={{ marginLeft: 2, transform: open ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s" }}>
							<path d="M1 1L4 4L7 1" stroke="#4b5563" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
						</svg>
						{open && (
							<div style={{ position: "absolute", top: "calc(100% + 6px)", left: 0, background: "#ffffff", border: "1px solid #E5E7EB", borderRadius: 10, boxShadow: "0 10px 25px rgba(0,0,0,0.12)", zIndex: 1000, minWidth: 160, padding: "6px 0", maxHeight: 200, overflowY: "auto" }}>
								{COUNTRIES.map((c) => (
									<button
										key={c.code}
										type="button"
										onClick={(e) => {
											e.stopPropagation();
											onCountryChange(c);
											setOpen(false);
											setTimeout(() => { trigger("phone"); }, 0);
										}}
										style={{ display: "flex", alignItems: "center", width: "100%", padding: "8px 14px", border: "none", background: country.code === c.code ? "#f3f4f6" : "#ffffff", cursor: "pointer", gap: 10, textAlign: "left" }}
									>
										{c.flag}
										<span style={{ fontSize: 13, fontWeight: 600, color: "#2d3035", flex: 1 }}>{c.label}</span>
										{country.code === c.code && <span style={{ color: "#00b159", fontWeight: "bold", fontSize: 14 }}>✓</span>}
									</button>
								))}
							</div>
						)}
					</div>
					<input
						type="tel"
						placeholder={country.placeholder}
						style={{ border: "none", outline: "none", height: "100%", width: "100%", paddingLeft: 12, fontSize: 14.5, fontWeight: 600, color: "var(--zoco-ink, #2d3035)", background: "transparent" }}
						{...register("phone", {
							required: "Ingresá tu teléfono.",
							validate: (val) => country.validate(val) || "Este número no es válido",
						})}
					/>
				</div>
				{err(errors.phone, { marginTop: 4, display: "block" })}
			</div>
			<div className={fieldCls(false)}>
				<label>DNI <span className="req">*</span></label>
				<div className="inp">
					<RIcon name="user" />
					<input
						placeholder="Tu DNI"
						onInput={(e) => {
							e.target.value = (e.target.value || "").replace(/\D/g, "").slice(0, 10);
						}}
						{...register("dni", {
							required: "Ingresá tu DNI.",
							pattern: { value: /^\d{7,10}$/, message: "DNI inválido (debe tener entre 7 y 10 números)." },
						})}
					/>
				</div>
				{err(errors.dni)}
			</div>
			{showNotes && (
				<div className={fieldCls(true)}>
					<label>Notas adicionales</label>
					<div className="inp" style={{ height: "auto", padding: "12px 14px" }}>
						<textarea
							placeholder={notesPlaceholder}
							rows={2}
							style={{ border: "none", outline: "none", width: "100%", resize: "none", fontFamily: "inherit", fontSize: 14 }}
							{...register("notes")}
						/>
					</div>
				</div>
			)}
		</>
	);
};

export default BuyerDataFields;
